import * as z from "@/lib/zod";
import { bookFacts, usStateSchema, type BookCatalog } from "@/lib/book-catalog";
import type { UsState } from "@/lib/us-states";

export const BOOK_LOCATION_FILTER_NAME = "location";

export type BookLocation = {
  readonly v: 1;
  readonly state: UsState | null;
  readonly hereOnly: boolean;
};

export const NO_BOOK_LOCATION: BookLocation = { v: 1, state: null, hereOnly: false };

export type SavedBookLocation = { readonly userId: string | null; readonly location: BookLocation | null };

export type BookPlace = {
  readonly state: UsState | null;
  readonly detected: UsState | null;
  readonly chosen: boolean;
  readonly hereOnly: boolean;
};

export type Availability = {
  readonly place: BookPlace;
  readonly isHere: (id: string) => boolean;
  readonly shown: <T extends string>(book: T, defaults: readonly T[]) => T | null;
};

const StoredLocation = z.strictObject({
  v: z.literal(1),
  state: usStateSchema.nullable(),
  hereOnly: z.boolean(),
});

export function parseBookLocation(value: unknown): BookLocation | null {
  const parsed = StoredLocation.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function availability(catalog: BookCatalog | null, location: BookLocation, detected: UsState | null): Availability {
  const state = location.state ?? detected;
  const hereOnly = catalog !== null && state !== null && location.hereOnly;
  const isHere = (id: string) => !hereOnly || (bookFacts(catalog, id)?.states.includes(state) ?? true);
  return {
    place: { state, detected, chosen: location.state !== null, hereOnly },
    isHere,
    shown: (book, defaults) => hereOnly && !isHere(book)
      ? defaults.find((id) => bookFacts(catalog, id)?.states.includes(state)) ?? defaults.find((id) => bookFacts(catalog, id) === undefined) ?? null
      : book,
  };
}

export const NOT_BETTABLE_CLASS = "text-zinc-500 opacity-60";

export const everywhere = (): boolean => true;

export function notHere(place: BookPlace, bettable: boolean): UsState | null {
  return place.hereOnly && !bettable ? place.state : null;
}

export function notBettableIn(name: string, state: UsState): string {
  return `${name}, not bettable in ${state}`;
}

export function keepHidden<T extends string>(next: readonly T[], current: readonly T[], isHere: (id: string) => boolean): T[] {
  return [...next.filter(isHere), ...current.filter((id) => !isHere(id))];
}
